import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import TrainOutlinedIcon from '@mui/icons-material/TrainOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BalanceIcon from '@mui/icons-material/Balance';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import type {
  MasterToolProjectResult,
  MasterToolUnit,
  PricePromoUnit,
  ThumbsupUnit,
} from '../../services/api';
import { COMPARE_CART_MAX } from './CompareCartDialog';

/**
 * Holographic "research console" card shown when a map marker is tapped.
 * Deliberately dark-on-dark with cyan accents in both app themes — it reads
 * as an instrument panel floating over the map rather than a plain dialog.
 */

export type MapDealFilter = 'none' | 'promo' | 'thumbsup';
type PopupTab = 'details' | 'search';

interface ProjectPopupCardProps {
  project: MasterToolProjectResult;
  pricePromoUnits: PricePromoUnit[];
  thumbsupUnits: ThumbsupUnit[];
  onClose: () => void;
  onViewDetails: (projectId: string) => void;
  /** Opens the Price History dialog for a promo unit (Project Details tab only). */
  onPricePromoUnitClick?: (unit: PricePromoUnit) => void;
  /** Navigates to the project and sets the unit as the reference unit (Project Details tab only). */
  onThumbsupUnitClick?: (unit: ThumbsupUnit) => void;
  /** Focus this project and open Super Ratio. Click again to turn off when active. */
  onSuperRatio?: () => void;
  /** When true, Super Ratio chip reads as active / toggle-off. */
  superRatioActive?: boolean;
  /** Add / remove this project from the Super Map compare cart. */
  onToggleCompare?: (event: React.MouseEvent) => void;
  compareInCart?: boolean;
  compareCartFull?: boolean;
  /** Criteria-search matched units for this project — enables the Search Results tab. */
  searchResultUnits?: MasterToolUnit[];
  searchResultsLoading?: boolean;
  searchResultsError?: string | null;
  promoUnitIds?: Set<number>;
  thumbsupUnitIds?: Set<number>;
  /** Prefer opening on Search Results when criteria search is active. */
  initialTab?: PopupTab;
  /** Collapse project body into a dropdown header (e.g. while a Super Ratio card is open). */
  bodyCollapsed?: boolean;
  onToggleBodyCollapsed?: () => void;
}

const BEDROOM_KEYS = ['br1', 'br2', 'br3', 'br4', 'br5', 'ph'] as const;
const BEDROOM_LABELS: Record<(typeof BEDROOM_KEYS)[number], string> = {
  br1: '1BR',
  br2: '2BR',
  br3: '3BR',
  br4: '4BR',
  br5: '5BR',
  ph: 'PH',
};

const CYAN = '#22d3ee';
const PROMO_ORANGE = '#fbbf24';
const THUMB_GREEN = '#4ade80';
const SUPER_RATIO_ORANGE = '#ed8936';
const COMPARE_VIOLET = '#a78bfa';

const formatCompactPrice = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value) || value <= 0) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${Math.round(value)}`;
};

const formatCount = (value: number | string | null | undefined): string => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-SG').format(n);
};

const formatSqft = (value: number | string | null | undefined): string => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (n == null || !Number.isFinite(n) || n <= 0) return '';
  return `${new Intl.NumberFormat('en-SG').format(Math.round(n))} sqft`;
};

const microLabelSx = {
  fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: 'rgba(148, 197, 222, 0.75)',
};

const EMPTY_UNIT_IDS = new Set<number>();

const MetaCell: React.FC<{ label: string; value: React.ReactNode; hint?: string | null }> = ({ label, value, hint }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.9,
      borderRadius: '10px',
      bgcolor: 'rgba(34, 211, 238, 0.055)',
      border: '1px solid rgba(34, 211, 238, 0.14)',
      minWidth: 0,
    }}
  >
    <Typography sx={microLabelSx}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#f1f7fb', lineHeight: 1.25, mt: 0.25 }} noWrap>
      {value ?? '—'}
    </Typography>
    {hint ? (
      <Typography sx={{ fontSize: 10.5, color: 'rgba(196, 220, 233, 0.6)', lineHeight: 1.2 }} noWrap>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

const ProjectPopupCard: React.FC<ProjectPopupCardProps> = ({
  project,
  pricePromoUnits,
  thumbsupUnits,
  onClose,
  onViewDetails,
  onPricePromoUnitClick,
  onThumbsupUnitClick,
  onSuperRatio,
  superRatioActive = false,
  onToggleCompare,
  compareInCart = false,
  compareCartFull = false,
  searchResultUnits,
  searchResultsLoading = false,
  searchResultsError = null,
  promoUnitIds,
  thumbsupUnitIds,
  initialTab = 'details',
  bodyCollapsed = false,
  onToggleBodyCollapsed,
}) => {
  const hasSearchTab = searchResultUnits != null;
  const [tab, setTab] = useState<PopupTab>(hasSearchTab ? initialTab : 'details');
  const canCollapse = Boolean(onToggleBodyCollapsed);
  const showBody = !canCollapse || !bodyCollapsed;

  useEffect(() => {
    setTab(hasSearchTab ? initialTab : 'details');
  }, [project.id, hasSearchTab, initialTab]);

  const availableUnits = useMemo(() => {
    let total = 0;
    let seen = false;
    for (const key of BEDROOM_KEYS) {
      const units = project[key]?.availableUnits;
      if (units != null && Number.isFinite(units)) {
        total += units;
        seen = true;
      }
    }
    return seen ? total : null;
  }, [project]);

  const totalUnits = useMemo(() => {
    const n = typeof project.totalUnits === 'string' ? parseFloat(project.totalUnits) : project.totalUnits;
    return n != null && Number.isFinite(n) && n > 0 ? n : null;
  }, [project.totalUnits]);

  const soldUnits = project.soldUnits ?? (totalUnits != null && availableUnits != null ? totalUnits - availableUnits : null);
  const soldPct = totalUnits && soldUnits != null ? Math.max(0, Math.min(100, (soldUnits / totalUnits) * 100)) : null;

  const bedroomChips = BEDROOM_KEYS.map((key) => ({
    key,
    label: BEDROOM_LABELS[key],
    units: project[key]?.availableUnits ?? null,
    fromPrice: project[key]?.fromPrice ?? null,
  })).filter((chip) => chip.units != null && chip.units > 0);

  const districtValue = project.district
    ? String(project.district).toUpperCase().startsWith('D')
      ? String(project.district).toUpperCase()
      : `D${project.district}`
    : '—';

  const showPromoList = pricePromoUnits.length > 0;
  const showThumbsList = thumbsupUnits.length > 0;
  const promoIds = promoUnitIds ?? EMPTY_UNIT_IDS;
  const thumbIds = thumbsupUnitIds ?? EMPTY_UNIT_IDS;

  const sortedSearchUnits = useMemo(() => {
    const units = searchResultUnits ?? [];
    return [...units].sort((a, b) =>
      a.unitNumber.localeCompare(b.unitNumber, undefined, { numeric: true, sensitivity: 'base' }),
    );
  }, [searchResultUnits]);

  const isExecutiveCondo = useMemo(() => {
    if (project.projectCategoryId === 'C1803') return true;
    return (project.projectCategory ?? '').toLowerCase().includes('executive condo');
  }, [project.projectCategoryId, project.projectCategory]);

  const searchCount = searchResultUnits?.length ?? 0;
  const hasUnitInventory =
    (availableUnits != null && availableUnits > 0) ||
    (totalUnits != null && totalUnits > 0) ||
    (soldUnits != null && soldUnits > 0) ||
    bedroomChips.length > 0;
  const compareDisabled = !compareInCart && compareCartFull;

  return (
    <Box
      className="mr-popup-card"
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 396,
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        color: '#eef6fa',
        background: 'linear-gradient(160deg, rgba(10, 18, 32, 0.94) 0%, rgba(13, 27, 42, 0.94) 55%, rgba(9, 22, 34, 0.96) 100%)',
        backdropFilter: 'blur(14px)',
        border: '1px solid rgba(34, 211, 238, 0.3)',
        boxShadow: '0 24px 64px rgba(2, 8, 20, 0.6), 0 0 32px rgba(34, 211, 238, 0.12), inset 0 1px 0 rgba(255,255,255,0.06)',
      }}
    >
      <Box className="mr-scanline" />
      {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
        <Box
          key={corner}
          sx={{
            position: 'absolute',
            width: 14,
            height: 14,
            zIndex: 3,
            pointerEvents: 'none',
            borderColor: 'rgba(34, 211, 238, 0.75)',
            borderStyle: 'solid',
            borderWidth: 0,
            ...(corner === 'tl' && { top: 6, left: 6, borderTopWidth: 2, borderLeftWidth: 2 }),
            ...(corner === 'tr' && { top: 6, right: 6, borderTopWidth: 2, borderRightWidth: 2 }),
            ...(corner === 'bl' && { bottom: 6, left: 6, borderBottomWidth: 2, borderLeftWidth: 2 }),
            ...(corner === 'br' && { bottom: 6, right: 6, borderBottomWidth: 2, borderRightWidth: 2 }),
          }}
        />
      ))}

      <Box sx={{ overflowY: 'auto', p: 2, pt: 1.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box className="mr-pulse-dot" />
          <Typography sx={{ ...microLabelSx, color: CYAN, flex: 1 }}>Project Intel · Super Map</Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(238, 246, 250, 0.7)', mr: -0.5, mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mt: 0.5,
            ...(canCollapse
              ? {
                  cursor: 'pointer',
                  borderRadius: '10px',
                  mx: -0.5,
                  px: 0.5,
                  py: 0.35,
                  '&:hover': { bgcolor: 'rgba(34, 211, 238, 0.06)' },
                }
              : null),
          }}
          onClick={canCollapse ? onToggleBodyCollapsed : undefined}
          role={canCollapse ? 'button' : undefined}
          aria-expanded={canCollapse ? showBody : undefined}
        >
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 21,
              fontWeight: 800,
              lineHeight: 1.15,
              background: 'linear-gradient(90deg, #ffffff 0%, #a5f3fc 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {project.name || 'Unnamed Project'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.55, flexShrink: 0 }}>
          {onSuperRatio && (
            <Tooltip
              title={superRatioActive ? 'Turn off Super Ratio' : 'Zoom here and open Super Ratio'}
              placement="top"
            >
              <Chip
                clickable
                size="small"
                icon={<BalanceIcon sx={{ fontSize: 14 }} />}
                label="Super Ratio"
                onClick={(e) => {
                  e.stopPropagation();
                  onSuperRatio();
                }}
                sx={{
                  flexShrink: 0,
                  height: 26,
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: superRatioActive ? '#04121d' : SUPER_RATIO_ORANGE,
                  bgcolor: superRatioActive ? SUPER_RATIO_ORANGE : 'rgba(237, 137, 54, 0.12)',
                  border: `1px solid ${superRatioActive ? SUPER_RATIO_ORANGE : 'rgba(237, 137, 54, 0.45)'}`,
                  '& .MuiChip-icon': { color: superRatioActive ? '#04121d' : SUPER_RATIO_ORANGE },
                  '&:hover': {
                    bgcolor: superRatioActive ? '#f6ad55' : 'rgba(237, 137, 54, 0.22)',
                  },
                }}
              />
            </Tooltip>
          )}
          {onToggleCompare && (
            <Tooltip
              title={
                compareInCart
                  ? 'Remove from compare'
                  : compareDisabled
                    ? `Compare cart is full (${COMPARE_CART_MAX}/${COMPARE_CART_MAX})`
                    : 'Add to Compare'
              }
              placement="top"
            >
              <span>
                <Chip
                  clickable={!compareDisabled}
                  size="small"
                  icon={<ShoppingCartOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={compareInCart ? 'Remove from compare' : 'Add to Compare'}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (compareDisabled) return;
                    onToggleCompare(e);
                  }}
                  sx={{
                    flexShrink: 0,
                    height: 'auto',
                    minHeight: 26,
                    py: 0.35,
                    fontSize: 10,
                    fontWeight: 800,
                    maxWidth: 132,
                    opacity: compareDisabled ? 0.45 : 1,
                    cursor: compareDisabled ? 'not-allowed' : 'pointer',
                    color: compareInCart ? '#04121d' : COMPARE_VIOLET,
                    bgcolor: compareInCart ? COMPARE_VIOLET : 'rgba(167, 139, 250, 0.12)',
                    border: `1px solid ${compareInCart ? COMPARE_VIOLET : 'rgba(167, 139, 250, 0.45)'}`,
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      lineHeight: 1.15,
                      px: 0.75,
                      textAlign: 'left',
                    },
                    '& .MuiChip-icon': { color: compareInCart ? '#04121d' : COMPARE_VIOLET, ml: 0.5 },
                    '&:hover': {
                      bgcolor: compareDisabled
                        ? compareInCart
                          ? COMPARE_VIOLET
                          : 'rgba(167, 139, 250, 0.12)'
                        : compareInCart
                          ? '#c4b5fd'
                          : 'rgba(167, 139, 250, 0.22)',
                    },
                  }}
                />
              </span>
            </Tooltip>
          )}
          </Box>
          {canCollapse && (
            <ExpandMoreIcon
              sx={{
                flexShrink: 0,
                mt: 0.35,
                color: 'rgba(165, 243, 252, 0.85)',
                fontSize: 22,
                transition: 'transform 0.22s ease',
                transform: showBody ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          )}
        </Box>

        <Collapse in={showBody} timeout={280} unmountOnExit={false}>
        <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.9 }}>
          {project.projectCategory && (
            <Chip
              size="small"
              label={project.projectCategory}
              sx={{
                height: 20,
                fontSize: 10.5,
                fontWeight: 600,
                color: '#a5f3fc',
                bgcolor: 'rgba(34, 211, 238, 0.12)',
                border: '1px solid rgba(34, 211, 238, 0.3)',
              }}
            />
          )}
          {pricePromoUnits.length > 0 && (
            <Chip
              size="small"
              icon={<LocalOfferIcon sx={{ fontSize: 12, color: `${PROMO_ORANGE} !important` }} />}
              label={`${pricePromoUnits.length} Price Promo`}
              sx={{
                height: 20,
                fontSize: 10.5,
                fontWeight: 700,
                color: PROMO_ORANGE,
                bgcolor: 'rgba(251, 191, 36, 0.12)',
                border: '1px solid rgba(251, 191, 36, 0.35)',
              }}
            />
          )}
          {thumbsupUnits.length > 0 && (
            <Chip
              size="small"
              icon={<ThumbUpIcon sx={{ fontSize: 12, color: `${THUMB_GREEN} !important` }} />}
              label={`${thumbsupUnits.length} Thumbsup`}
              sx={{
                height: 20,
                fontSize: 10.5,
                fontWeight: 700,
                color: THUMB_GREEN,
                bgcolor: 'rgba(74, 222, 128, 0.12)',
                border: '1px solid rgba(74, 222, 128, 0.35)',
              }}
            />
          )}
        </Box>

        {hasSearchTab && (
          <Box
            sx={{
              display: 'flex',
              mt: 1.4,
              borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
            }}
          >
            {(
              [
                { id: 'details' as const, label: 'Project Details' },
                {
                  id: 'search' as const,
                  label: searchResultsLoading
                    ? 'Search Results · …'
                    : `Search Results · ${searchCount}`,
                },
              ] as const
            ).map((item) => {
              const active = tab === item.id;
              return (
                <Box
                  key={item.id}
                  component="button"
                  type="button"
                  onClick={() => setTab(item.id)}
                  sx={{
                    flex: 1,
                    position: 'relative',
                    appearance: 'none',
                    border: 0,
                    outline: 'none',
                    cursor: 'pointer',
                    bgcolor: 'transparent',
                    px: 1,
                    py: 1,
                    fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: active ? CYAN : 'rgba(196, 220, 233, 0.55)',
                    transition: 'color 0.18s ease',
                    '&:hover': {
                      color: active ? CYAN : 'rgba(238, 246, 250, 0.85)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 8,
                      right: 8,
                      bottom: -1,
                      height: 2,
                      borderRadius: 1,
                      background: active
                        ? 'linear-gradient(90deg, transparent 0%, #22d3ee 20%, #67e8f9 50%, #22d3ee 80%, transparent 100%)'
                        : 'transparent',
                      boxShadow: active ? '0 0 10px rgba(34, 211, 238, 0.65)' : 'none',
                      transition: 'background 0.18s ease, box-shadow 0.18s ease',
                    },
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>
        )}

        {tab === 'details' && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.9, mt: 1.5 }}>
              <MetaCell label="District" value={districtValue} hint={project.districtLocation} />
              <MetaCell label="Region" value={project.area || '—'} />
              <MetaCell label="TOP" value={project.topDate ? String(project.topDate) : '—'} />
              <MetaCell label="Tenure" value={project.tenure || '—'} />
            </Box>

            {project.address && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.25 }}>
                <PlaceOutlinedIcon sx={{ fontSize: 15, color: CYAN, mt: '1px' }} />
                <Typography sx={{ fontSize: 12, color: 'rgba(222, 238, 246, 0.85)', lineHeight: 1.4 }}>
                  {project.address}
                </Typography>
              </Box>
            )}
            {project.nearestMrt && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                <TrainOutlinedIcon sx={{ fontSize: 15, color: CYAN }} />
                <Typography sx={{ fontSize: 12, color: 'rgba(222, 238, 246, 0.85)' }}>
                  {project.nearestMrt}
                  {project.walkingTimeToMrt ? ` · ${project.walkingTimeToMrt} min walk` : ''}
                </Typography>
              </Box>
            )}

            {hasUnitInventory && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: '12px',
                border: '1px solid rgba(34, 211, 238, 0.18)',
                background: 'linear-gradient(180deg, rgba(34, 211, 238, 0.07) 0%, rgba(34, 211, 238, 0.02) 100%)',
              }}
            >
              <Typography sx={microLabelSx}>Unit Inventory</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mt: 0.5 }}>
                <Box>
                  <Typography sx={{ fontSize: 24, fontWeight: 800, color: CYAN, lineHeight: 1 }}>
                    {formatCount(availableUnits)}
                  </Typography>
                  <Typography sx={{ ...microLabelSx, fontSize: 9 }}>Available</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#fbbf24', lineHeight: 1 }}>
                    {formatCount(soldUnits)}
                  </Typography>
                  <Typography sx={{ ...microLabelSx, fontSize: 9 }}>Sold</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 24, fontWeight: 800, color: 'rgba(238,246,250,0.9)', lineHeight: 1 }}>
                    {formatCount(totalUnits)}
                  </Typography>
                  <Typography sx={{ ...microLabelSx, fontSize: 9 }}>Total</Typography>
                </Box>
              </Box>
              {soldPct != null && (
                <Box sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(34, 211, 238, 0.12)', overflow: 'hidden' }}>
                  <Box
                    className="mr-progress-fill"
                    sx={{
                      height: '100%',
                      width: `${soldPct}%`,
                      borderRadius: 3,
                      background: 'linear-gradient(90deg, #22d3ee 0%, #fbbf24 100%)',
                    }}
                  />
                </Box>
              )}
              {soldPct != null && (
                <Typography sx={{ fontSize: 10.5, color: 'rgba(196, 220, 233, 0.65)', mt: 0.4 }}>
                  {soldPct.toFixed(0)}% sold
                </Typography>
              )}
              {bedroomChips.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 1 }}>
                  {bedroomChips.map((chip) => (
                    <Box
                      key={chip.key}
                      sx={{
                        px: 0.9,
                        py: 0.35,
                        borderRadius: '8px',
                        border: '1px solid rgba(34, 211, 238, 0.22)',
                        bgcolor: 'rgba(10, 24, 38, 0.6)',
                      }}
                    >
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#e3f4fb', lineHeight: 1.2 }}>
                        {chip.label} · {chip.units}
                      </Typography>
                      {chip.fromPrice ? (
                        <Typography sx={{ fontSize: 9.5, color: 'rgba(165, 243, 252, 0.8)', lineHeight: 1.2 }}>
                          fr {formatCompactPrice(chip.fromPrice)}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            )}

            {showPromoList && (
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ ...microLabelSx, color: PROMO_ORANGE, mb: 0.6 }}>
                  Price Promo Units · {pricePromoUnits.length}
                </Typography>
                <Box sx={{ maxHeight: 168, overflowY: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {pricePromoUnits.map((unit) => (
                    <Box
                      key={`promo-${unit.id}`}
                      role={onPricePromoUnitClick ? 'button' : undefined}
                      tabIndex={onPricePromoUnitClick ? 0 : undefined}
                      onClick={() => onPricePromoUnitClick?.(unit)}
                      onKeyDown={(e) => {
                        if (!onPricePromoUnitClick) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onPricePromoUnitClick(unit);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.6,
                        borderRadius: '9px',
                        border: '1px solid rgba(251, 191, 36, 0.22)',
                        bgcolor: 'rgba(251, 191, 36, 0.06)',
                        cursor: onPricePromoUnitClick ? 'pointer' : 'default',
                        transition: 'background 0.15s, border-color 0.15s',
                        ...(onPricePromoUnitClick
                          ? {
                              '&:hover': {
                                bgcolor: 'rgba(251, 191, 36, 0.14)',
                                borderColor: 'rgba(251, 191, 36, 0.45)',
                              },
                            }
                          : {}),
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fef3c7', minWidth: 62 }}>
                        {unit.UnitNumber}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: 'rgba(222, 238, 246, 0.65)', flex: 1 }} noWrap>
                        {unit.bedroomNumber ? `${unit.bedroomNumber}BR` : ''}
                        {formatSqft(unit.size) ? ` · ${formatSqft(unit.size)}` : ''}
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>
                          {formatCompactPrice(unit.price)}
                        </Typography>
                        {Number.isFinite(unit.priceChange) && unit.priceChange < 0 && (
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: PROMO_ORANGE }}>
                            ▼ {formatCompactPrice(Math.abs(unit.priceChange))}
                            {Number.isFinite(unit.priceChangePercent)
                              ? ` (${Math.abs(unit.priceChangePercent).toFixed(1)}%)`
                              : ''}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {showThumbsList && (
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ ...microLabelSx, color: THUMB_GREEN, mb: 0.6 }}>
                  Thumbsup Units · {thumbsupUnits.length}
                </Typography>
                <Box sx={{ maxHeight: 168, overflowY: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {thumbsupUnits.map((unit) => (
                    <Box
                      key={`thumb-${unit.id}`}
                      role={onThumbsupUnitClick ? 'button' : undefined}
                      tabIndex={onThumbsupUnitClick ? 0 : undefined}
                      onClick={() => onThumbsupUnitClick?.(unit)}
                      onKeyDown={(e) => {
                        if (!onThumbsupUnitClick) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onThumbsupUnitClick(unit);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.6,
                        borderRadius: '9px',
                        border: '1px solid rgba(74, 222, 128, 0.22)',
                        bgcolor: 'rgba(74, 222, 128, 0.06)',
                        cursor: onThumbsupUnitClick ? 'pointer' : 'default',
                        transition: 'background 0.15s, border-color 0.15s',
                        ...(onThumbsupUnitClick
                          ? {
                              '&:hover': {
                                bgcolor: 'rgba(74, 222, 128, 0.14)',
                                borderColor: 'rgba(74, 222, 128, 0.45)',
                              },
                            }
                          : {}),
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#dcfce7', minWidth: 62 }}>
                        {unit.UnitNumber}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: 'rgba(222, 238, 246, 0.65)', flex: 1 }} noWrap>
                        {unit.bedroomNumber ? `${unit.bedroomNumber}BR` : ''}
                        {formatSqft(unit.size) ? ` · ${formatSqft(unit.size)}` : ''}
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: THUMB_GREEN }}>
                        👍 {unit.thumbsupCount}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Button
              fullWidth
              endIcon={<ArrowForwardIcon />}
              onClick={() => project.id && onViewDetails(project.id)}
              disabled={!project.id}
              sx={{
                mt: 1.75,
                py: 1,
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#04121d',
                borderRadius: '10px',
                background: 'linear-gradient(90deg, #22d3ee 0%, #60a5fa 100%)',
                boxShadow: '0 4px 18px rgba(34, 211, 238, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #67e8f9 0%, #93c5fd 100%)',
                  boxShadow: '0 6px 22px rgba(34, 211, 238, 0.5)',
                },
              }}
            >
              View Full Research
            </Button>
          </>
        )}

        {tab === 'search' && hasSearchTab && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ ...microLabelSx, color: 'rgba(165, 243, 252, 0.85)', mb: 0.75 }}>
              {searchResultsLoading
                ? 'Resolving matches…'
                : `${searchCount} unit${searchCount === 1 ? '' : 's'} matched`}
            </Typography>

            {searchResultsLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, py: 3.5 }}>
                <CircularProgress size={26} sx={{ color: CYAN }} />
                <Typography sx={{ ...microLabelSx, color: CYAN }}>Syncing unit matrix…</Typography>
              </Box>
            )}

            {!searchResultsLoading && searchResultsError && (
              <Typography sx={{ fontSize: 12.5, color: '#f87171', fontWeight: 600 }}>{searchResultsError}</Typography>
            )}

            {!searchResultsLoading && !searchResultsError && (
              <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 0.55 }}>
                {sortedSearchUnits.length === 0 ? (
                  <Typography sx={{ fontSize: 12.5, color: 'rgba(222, 238, 246, 0.7)', py: 2 }}>
                    No matching units found for this project.
                  </Typography>
                ) : (
                  sortedSearchUnits.map((unit) => {
                    const isPromo = promoIds.has(unit.id);
                    const isThumb = thumbIds.has(unit.id);
                    const rowSx =
                      isPromo && isThumb
                        ? {
                            border: '1px solid rgba(251, 191, 36, 0.35)',
                            background:
                              'linear-gradient(90deg, rgba(251, 191, 36, 0.12) 0%, rgba(74, 222, 128, 0.12) 100%)',
                          }
                        : isPromo
                          ? {
                              border: '1px solid rgba(251, 191, 36, 0.28)',
                              bgcolor: 'rgba(251, 191, 36, 0.08)',
                            }
                          : isThumb
                            ? {
                                border: '1px solid rgba(74, 222, 128, 0.28)',
                                bgcolor: 'rgba(74, 222, 128, 0.08)',
                              }
                            : {
                                border: '1px solid rgba(34, 211, 238, 0.14)',
                                bgcolor: 'rgba(34, 211, 238, 0.03)',
                              };

                    return (
                      <Box
                        key={unit.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1,
                          py: 0.7,
                          borderRadius: '9px',
                          cursor: 'default',
                          ...rowSx,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: isPromo ? '#fef3c7' : isThumb ? '#dcfce7' : '#e0f2fe',
                            minWidth: 58,
                          }}
                        >
                          {unit.unitNumber}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'rgba(222, 238, 246, 0.65)', flex: 1 }} noWrap>
                          {[
                            unit.bedroom ? `${unit.bedroom}BR` : '',
                            unit.bathroom ? `${unit.bathroom}Bath` : '',
                            formatSqft(unit.areaSqft),
                            unit.psf ? `$${Math.round(unit.psf)} psf` : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                          {isPromo && (
                            <Box
                              title="Price Promo"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 0.35,
                                color: PROMO_ORANGE,
                                bgcolor: 'rgba(251, 191, 36, 0.16)',
                                border: '1px solid rgba(251, 191, 36, 0.45)',
                                borderRadius: '6px',
                              }}
                            >
                              <LocalOfferIcon sx={{ fontSize: 13 }} />
                            </Box>
                          )}
                          {isThumb && (
                            <Box
                              title="Thumbsup"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 0.35,
                                color: THUMB_GREEN,
                                bgcolor: 'rgba(74, 222, 128, 0.16)',
                                border: '1px solid rgba(74, 222, 128, 0.45)',
                                borderRadius: '6px',
                              }}
                            >
                              <ThumbUpIcon sx={{ fontSize: 13 }} />
                            </Box>
                          )}
                        </Box>
                        {isExecutiveCondo ? (
                          <Box sx={{ textAlign: 'right', minWidth: 72 }}>
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 800,
                                color: isPromo ? PROMO_ORANGE : isThumb ? THUMB_GREEN : '#fff',
                                lineHeight: 1.2,
                              }}
                            >
                              NPS {formatCompactPrice(unit.price)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: 'rgba(222, 238, 246, 0.7)',
                                lineHeight: 1.2,
                              }}
                            >
                              DPS{' '}
                              {unit.priceDps != null && unit.priceDps > 0
                                ? formatCompactPrice(unit.priceDps)
                                : '—'}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 800,
                              color: isPromo ? PROMO_ORANGE : isThumb ? THUMB_GREEN : '#fff',
                              minWidth: 52,
                              textAlign: 'right',
                            }}
                          >
                            {formatCompactPrice(unit.price)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            )}
          </Box>
        )}
        </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default ProjectPopupCard;
